/**
 * FeaturesSection
 *
 * Tabbed feature highlights with theme-aware screenshots/GIFs and CTA,
 * optimized for Safari scroll performance.
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
// Removed unused imports to avoid lint errors and reduce bundle size

// Complete browser skeleton with fixed dimensions to prevent any size changes
const CompleteBrowserSkeleton = () => (
  <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 w-full max-w-none">
    {/* Browser top bar */}
    <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
      <div className="flex space-x-2">
        <div className="w-3 h-3 rounded-full bg-red-500"></div>
        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
        <div className="w-3 h-3 rounded-full bg-green-500"></div>
      </div>
      <div className="ml-auto flex items-center text-xs text-gray-500 dark:text-gray-400">
        <div className="w-3 h-3 border border-purple-500 border-t-transparent rounded-full animate-spin mr-2"></div>
        Loading...
      </div>
    </div>
    
    {/* Fixed-size content area that matches typical GIF dimensions */}
    <div className="relative bg-gray-50 dark:bg-gray-800" style={{ width: '100%', height: '600px', minHeight: '600px' }}>
      <div className="absolute inset-0 bg-gray-300 dark:bg-gray-600 animate-pulse">
      </div>
    </div>
  </div>
);

const screenshots = [
  {
    id: 'templates',
    title: 'Design-Ready Templates',
    description: 'From ballot initiatives to budget explainers, we\'ve got a template for that. Edit in Canva, launch fast and make an impact with proven designs.',
    image: '/images/screenshots/landing_gif1.gif',
    darkImage: '/images/screenshots/landing_gif1-dark.gif',
    loopDurationMs: 8000,
    features: [
      '500+ ready-to-customize templates',
      'One-click Canva integration',
      'Multiple formats for every platform',
      'Based on high-performing political creative'
    ]
  },
  {
    id: 'template-details',
    title: 'Edit in Canva',
    description: 'Dive into the details of each template with comprehensive background information and seamless editing capabilities.',
    image: '/images/screenshots/landing_gif2.gif',
    darkImage: '/images/screenshots/landing_gif2-dark.gif',
    loopDurationMs: 8000,
    features: [
      'View original ad copy and messaging',
      'See the organization behind successful ads',
      'One-click access to Canva template',
      'Links to original campaigns'
    ]
  },
  {
    id: 'ai-tools',
    title: 'AI-Powered Creative Tools',
    description: 'Harness the power of AI to analyze and perfect your political messaging. Evaluate ad copy effectiveness and optimize before launch.',
    image: '/images/screenshots/landing_gif3.gif',
    darkImage: '/images/screenshots/landing_gif3-dark.gif',
    loopDurationMs: 8000,
    features: [
      'Analyze ad copy for effectiveness',
      'AI-powered political messaging insights',
      'Audience-specific tone optimization',
      'Performance prediction analytics'
    ]
  },
  {
    id: 'video-testimonials',
    title: 'Gather Supporter Video',
    description: 'Collect, manage, and leverage supporter stories with our comprehensive video collecting solution designed for advocacy and political campaigns.',
    image: '/images/screenshots/landing_gif4.gif',
    darkImage: '/images/screenshots/landing_gif4-dark.gif',
    loopDurationMs: 8000,
    features: [
      'Simple video collection links',
      'Customizable supporter surveys',
      'Campaign performance analytics',
      'Seamless in-browser video uploads for supporters'
    ]
  },
];

export function FeaturesSection({ hideHeader = false, compactTop = false, ctaText, ctaTo, mediaOverrides, staticImages = false, ctaLarge = false }) {
  // State management
  const [activeTab, setActiveTab] = useState(screenshots[0]?.id || 'create');
  const initialDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [isDarkMode, setIsDarkMode] = useState(initialDark);
  const [imageLoading, setImageLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const initialScreenshot = screenshots[0];
  const TRANSPARENT_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  const [currentSrc, setCurrentSrc] = useState(TRANSPARENT_GIF);
  const imgRef = React.useRef(null);
  const activeScreenshot = screenshots.find(screenshot => screenshot.id === activeTab);

  const getOverriddenMedia = (id) => {
    if (!mediaOverrides) return null;
    return mediaOverrides[id] || null;
  };

  const getBaseSrcForId = (id, isDark) => {
    const sc = screenshots.find(s => s.id === id) || screenshots[0];
    const override = getOverriddenMedia(id);
    if (override) {
      // If only image provided, use it for both themes
      const overrideLight = override.image;
      const overrideDark = override.darkImage || override.image;
      return isDark ? overrideDark : overrideLight;
    }
    return isDark ? sc.darkImage : sc.image;
  };

  // Check if image is already cached
  const isImageCached = (imageSrc) => {
    return loadedImages.has(imageSrc);
  };

  // Monitor dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Check initial dark mode
    checkDarkMode();

    // Listen for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Preload images and check what's already cached
  useEffect(() => {
    const cached = new Set();
    
    screenshots.forEach(screenshot => {
      // Check both light and dark versions
      const lightImageCached = !!document.querySelector(`img[src="${screenshot.image}"]`);
      const darkImageCached = !!document.querySelector(`img[src="${screenshot.darkImage}"]`);
      
      if (lightImageCached) cached.add(screenshot.image);
      if (darkImageCached) cached.add(screenshot.darkImage);
    });
    
    setLoadedImages(cached);
    console.log('Cached images in FeaturesSection:', cached.size);
  }, []);

  // Load media for current tab; optionally avoid cache-buster for static images
  useEffect(() => {
    const baseSrc = getBaseSrcForId(activeTab, isDarkMode);
    const nextSrc = staticImages ? baseSrc : `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}r=${Date.now()}`;
    setImageLoading(true);
    setCurrentSrc(nextSrc);
  }, [isDarkMode, activeTab]);

  // Handle tab switching with simplified loading state management
  const handleTabSwitch = (screenshotId) => {
    const screenshot = screenshots.find(s => s.id === screenshotId);
    if (!screenshot) return;
    const baseSrc = getBaseSrcForId(screenshotId, isDarkMode);
    const nextSrc = staticImages ? baseSrc : `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}r=${Date.now()}`;
    setImageLoading(true);
    setActiveTab(screenshotId);
    setCurrentSrc(nextSrc);
  };

  // NOTE: Removed dynamic height adjustments to prevent layout shifts during scroll

  const topPaddingClass = compactTop ? 'pt-6 sm:pt-8 pb-24 sm:pb-32' : 'py-24 sm:py-32';

  return (
    <section id="features" className={`relative bg-white dark:bg-gray-900 ${topPaddingClass} px-2 sm:px-6 lg:px-8 scroll-mt-40 no-scroll-anchor`}>
      {/* Background decoration - optimized for Safari scroll performance */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-sm xl:-top-6" aria-hidden="true">
          <div
            className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-purple-200 to-indigo-300 opacity-10 dark:from-purple-800 dark:to-indigo-900 dark:opacity-15"
            style={{
              // Simplified shape for better performance
              borderRadius: '50%',
              transform: 'translateZ(0)', // Safari optimization
              willChange: 'auto'
            }}
          />
        </div>
      </div>
      
      {!hideHeader && (
        <div className="mx-auto max-w-3xl sm:max-w-4xl md:mx-auto md:text-center xl:max-w-5xl mb-16">
          <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Features</h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
            Powerful tools for political creators
          </p>
          <p className="mt-6 text-lg text-gray-600 dark:text-gray-400 mx-auto text-center max-w-[90%]">
            Our comprehensive platform helps you create professional-quality content that resonates with voters and supporters, all backed by real campaign data.
          </p>
        </div>
      )}
      
      {/* Tab navigation */}
      <div className="mb-12 max-w-2xl mx-auto">
        <div className="grid grid-cols-2 gap-2 px-4 sm:flex sm:flex-wrap sm:justify-center">
          {screenshots.map((screenshot) => {
            // Create shorter names for the buttons
            let shortName;
            switch(screenshot.id) {
              case 'templates':
                shortName = 'Templates';
                break;
              case 'template-details':
                shortName = 'Canva';
                break;
              case 'ai-tools':
                shortName = 'AI Tools';
                break;
              case 'video-testimonials':
                shortName = 'Supporter Stories';
                break;
              default:
                shortName = screenshot.title;
            }
            
            const isActive = activeTab === screenshot.id;
            const imageToCheck = getBaseSrcForId(screenshot.id, isDarkMode);
            const isCached = isImageCached(imageToCheck);
            
            return (
              <div 
                key={screenshot.id}
                className="relative"
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTabBg"
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600"
                    initial={false}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <button
                  onClick={() => handleTabSwitch(screenshot.id)}
                  className={`relative z-10 w-full sm:w-auto px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-white'
                      : 'text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400'
                  } focus:outline-none appearance-none`}
                  style={{ 
                    WebkitAppearance: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {shortName}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Feature content with screenshot */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col-reverse lg:flex-row items-center gap-8 lg:gap-12">
          {/* Text content */}
          <div className="w-full lg:w-4/12 space-y-6 lg:space-y-8 mt-8 lg:mt-0">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {activeScreenshot.title}
              </h3>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
                {activeScreenshot.description}
              </p>
            </div>
            
            <div className="space-y-3">
              {activeScreenshot.features.map((feature) => (
                <div key={feature} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <p className="text-base text-gray-700 dark:text-gray-300">
                    {feature}
                  </p>
                </div>
              ))}
            </div>
            
            <div>
            <Link 
              to={ctaTo || (activeTab === 'templates' || activeTab === 'template-details' ? "/demo-templates" : "/signup")}
                className={ctaLarge
                  ? "inline-flex items-center justify-center rounded-md bg-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 w-full sm:w-auto"
                  : "inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"}
              >
              {ctaText || (activeTab === 'templates' || activeTab === 'template-details' ? "See templates" : "Get started")}
              {!ctaLarge && (
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
              </Link>
            </div>
          </div>
          
          {/* Screenshot with loading state and fade transitions */}
          <div className="w-full lg:w-8/12">
            <div className="w-full">
              {/* Browser mockup that fills the full width */}
              <div className="w-full bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Browser top bar */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  {imageLoading && (
                    <div className="ml-auto flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <div className="w-3 h-3 border border-purple-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                      Loading...
                    </div>
                  )}
                </div>
                
                {/* Content area without forced minHeight to avoid container issues */}
                <div className="relative bg-gray-50 dark:bg-gray-800">
                  {/* Screenshot image */}
                  <img
                    ref={imgRef}
                    src={currentSrc}
                    alt={activeScreenshot.title}
                    width={1600}
                    height={900}
                    className={`w-full h-auto mx-auto transition-opacity duration-300 ${imageLoading ? 'opacity-60' : 'opacity-100'}`}
                    decoding="async"
                    loading="eager"
                    onLoad={() => setImageLoading(false)}
                  />
                  
                  {/* Optional dimming already handled by opacity */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 